import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

// Add consistent styling
const addConsistentStyles = () => {
  if (document.getElementById('universal-header-styles')) return;
  
  const style = document.createElement('style');
  style.id = 'universal-header-styles';
  style.textContent = `
    .font-poppins { font-family: 'Poppins', sans-serif; }
    .font-opensans { font-family: 'Open Sans', sans-serif; }
    .text-primary { color: #1E3A8A; }
    .bg-primary { background-color: #1E3A8A; }
    .text-accent { color: #F97316; }
    .bg-accent { background-color: #F97316; }
    .bg-bg-cream { background-color: #FFF7F0; }
    .text-text-dark { color: #333333; }
    .border-primary { border-color: #1E3A8A; }
    .hover\\:bg-primary:hover { background-color: #1E3A8A; }
    .hover\\:text-primary:hover { color: #1E3A8A; }
    .hover\\:bg-orange-600:hover { background-color: #ea580c; }
    .hover\\:bg-blue-800:hover { background-color: #1e40af; }
  `;
  document.head.appendChild(style);
};

const UniversalHeader = ({
  currentPage = 'dashboard',
  breadcrumbs = [],
  // Reuse existing navigation handlers from parent components
  onBackToDashboard,
  onNavigateToLearningPaths, 
  onNavigateToAdmin,
  onNavigateToQuestions,
  // Additional context
  selectedTopic = null,
  practiceSessionActive = false
}) => {
  const { user, userProfile, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Add consistent styles
  useEffect(() => {
    addConsistentStyles();
    
    // Add Google Fonts
    if (!document.getElementById('universal-header-fonts')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'universal-header-fonts';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Open+Sans:wght@400;500;600&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
  }, []);

  // Generate context-aware breadcrumbs
  const generateBreadcrumbs = () => {
    const baseBreadcrumbs = [
      { label: 'Dashboard', action: onBackToDashboard, current: currentPage === 'dashboard' }
    ];

    if (currentPage === 'learning-paths' || currentPage === 'topic' || currentPage === 'practice') {
      baseBreadcrumbs.push({
        label: 'Learning Paths',
        action: onNavigateToLearningPaths,
        current: currentPage === 'learning-paths'
      });
    }

    if (currentPage === 'topic' || currentPage === 'practice') {
      baseBreadcrumbs.push({
        label: selectedTopic?.name || 'Topic',
        action: null, // Current topic, no action needed
        current: currentPage === 'topic'
      });
    }

    if (currentPage === 'practice') {
      baseBreadcrumbs.push({
        label: 'Practice Session',
        action: null,
        current: true
      });
    }

    if (currentPage === 'admin') {
      baseBreadcrumbs.push({
        label: 'Admin Panel',
        action: onNavigateToAdmin,
        current: true
      });
    }

    // Use custom breadcrumbs if provided, otherwise use generated ones
    return breadcrumbs.length > 0 ? breadcrumbs : baseBreadcrumbs;
  };

  const handleSignOut = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      signOut();
    }
  };

  const isAdmin = userProfile?.role === 'admin';
  const userGrade = userProfile?.grade || 'N/A';
  const userName = userProfile?.first_name || user?.email?.split('@')[0] || 'User';

  return (
    <header className="bg-white shadow-sm border-b border-blue-100 sticky top-0 z-50 font-opensans">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          
          {/* Logo and Brand */}
          <div className="flex items-center">
            <div className="flex items-center">
              <img
                src="/maths_logo_teal.png"
                alt="MathsCampus Logo"
                className="h-10 w-auto"
                onError={(e) => {
                  // Fallback if logo image doesn't load
                  e.target.style.display = 'none';
                }}
              />
              <span className="ml-3 text-xl font-poppins font-bold text-primary">
                MathsCampus
              </span>
            </div>
          </div>

          {/* Navigation Breadcrumbs - Desktop */}
          <div className="hidden md:flex items-center space-x-1 text-sm">
            {generateBreadcrumbs().map((breadcrumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <span className="text-gray-400 mx-2">›</span>
                )}
                {breadcrumb.action && !breadcrumb.current ? (
                  <button
                    onClick={breadcrumb.action}
                    className="text-text-dark hover:text-primary transition-colors px-2 py-1 rounded hover:bg-gray-100"
                  >
                    {breadcrumb.label}
                  </button>
                ) : (
                  <span className={`px-2 py-1 rounded ${
                    breadcrumb.current 
                      ? 'bg-blue-100 text-primary font-semibold' 
                      : 'text-gray-600'
                  }`}>
                    {breadcrumb.label}
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Right Actions - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            
            {/* User Info */}
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-sm font-semibold text-text-dark">
                  {userName}
                </div>
                <div className="text-xs text-gray-600">
                  {userGrade}
                </div>
              </div>
            </div>

            {/* Admin Panel Access */}
            {isAdmin && onNavigateToAdmin && currentPage !== 'admin' && (
              <button
                onClick={onNavigateToAdmin}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                title="Access Admin Panel"
              >
                Admin
              </button>
            )}

            {/* Sign Out */}
            <button
              onClick={handleSignOut}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              Sign Out
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="space-y-4">
              
              {/* Mobile Breadcrumbs */}
              <div className="space-y-2">
                {generateBreadcrumbs().map((breadcrumb, index) => (
                  breadcrumb.action && !breadcrumb.current ? (
                    <button
                      key={index}
                      onClick={() => {
                        breadcrumb.action();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left text-text-dark hover:text-primary px-2 py-2 rounded hover:bg-gray-100"
                    >
                      {breadcrumb.label}
                    </button>
                  ) : (
                    <div
                      key={index}
                      className={`px-2 py-2 ${
                        breadcrumb.current 
                          ? 'bg-blue-100 text-primary font-semibold rounded' 
                          : 'text-gray-600'
                      }`}
                    >
                      {breadcrumb.label}
                    </div>
                  )
                ))}
              </div>

              {/* Mobile User Info */}
              <div className="border-t pt-4">
                <div className="px-2 py-2">
                  <div className="text-sm font-semibold text-text-dark">
                    {userName}
                  </div>
                  <div className="text-xs text-gray-600">
                    {userGrade}
                  </div>
                </div>

                {/* Mobile Admin Access */}
                {isAdmin && onNavigateToAdmin && currentPage !== 'admin' && (
                  <button
                    onClick={() => {
                      onNavigateToAdmin();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-semibold mb-2"
                  >
                    Admin Panel
                  </button>
                )}

                {/* Mobile Sign Out */}
                <button
                  onClick={() => {
                    handleSignOut();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left bg-gray-500 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default UniversalHeader;